import React, { useState } from 'react';
import InfoTip from '../../shared/InfoTip';
import { SkeletonText } from '../../shared/Skeleton';
import { ErrorState } from '../../shared/StateMessage';
import { fmtCurrency, fmtPercent } from '../../shared/format';
import { ExportMenu, downloadCsv, downloadPdf, makeExport } from '../shared';
import Pagination from '../../shared/Pagination';
import ThinScroll from '../../shared/ThinScroll';

// 25/page — the page already loads all rows (loadWastedSpend concatenates every
// RPC page into wastedSpend.items), so this is a pure client-side slice. 25 keeps
// each page to ~2 screens instead of a long scroll.
const PER_PAGE = 25;

// Export spec — one column list drives CSV + PDF. CSV emits raw values + full
// headers; PDF emits formatted clicks/impressions/CTR/cost + abbreviated headers.
// Conversions stay raw in both (they're always 0 here — non-converting terms).
const WASTED_EXPORT_COLUMNS = [
  { header: 'Search Term', get: (term) => term.search_term },
  { header: 'Campaign', get: (term) => term.campaign_name },
  { header: 'Ad Group', get: (term) => term.adgroup_name },
  { header: 'Clicks', get: (term) => term.clicks, pdf: (term) => term.clicks.toLocaleString() },
  { header: 'Impressions', pdfHeader: 'Impr.', get: (term) => term.impressions, pdf: (term) => term.impressions.toLocaleString() },
  { header: 'CTR', get: (term) => (term.ctr * 100).toFixed(2) + '%', pdf: (term) => fmtPercent(term.ctr) },
  { header: 'Cost', get: (term) => term.cost, pdf: (term) => fmtCurrency(term.cost) },
  { header: 'Conversions', pdfHeader: 'Conv.', get: (term) => term.conversions },
];
const WASTED_EXPORT = makeExport(WASTED_EXPORT_COLUMNS);

const NonConvertingTerms = ({ wastedSpend, loading, error, rangeStart, rangeEnd }) => {
  const [page, setPage] = useState(1);

  const wastedItems = wastedSpend?.items || [];
  const totalItems = wastedItems.length;
  const totalPages = Math.ceil(wastedItems.length / PER_PAGE) || 1;
  const pagedItems = wastedItems.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  // Reset page when data changes
  React.useEffect(() => { setPage(1); }, [wastedSpend]);

  const exportCsv = () => {
    if (!wastedItems.length) return;
    downloadCsv(WASTED_EXPORT.headers, wastedItems.map(WASTED_EXPORT.toCsvRow), `non-converting-terms-${rangeStart}-to-${rangeEnd}.csv`);
  };
  const exportPdf = () => {
    if (!wastedItems.length) return;
    downloadPdf('Non-Converting Search Terms', WASTED_EXPORT.pdfHeaders, wastedItems.map(WASTED_EXPORT.toPdfRow), `non-converting-terms-${rangeStart}-to-${rangeEnd}.pdf`, `${rangeStart} to ${rangeEnd}`);
  };

  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3 font-semibold inline-flex items-center">
        Spend Without Conversions — Review Recommended
        <InfoTip text="Search terms that drew clicks but produced no conversions in the range. Each row is a candidate to add as a negative keyword." />
      </div>
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-4 sm:p-6 relative">
        {wastedItems.length > 0 && (
          <div className="absolute top-4 right-4"><ExportMenu onCsv={exportCsv} onPdf={exportPdf} /></div>
        )}
        {error ? (
          <ErrorState message={error} />
        ) : loading ? (
          <div className="py-4"><SkeletonText lines={10} /></div>
        ) : !wastedSpend?.items?.length ? (
          <div className="flex items-center justify-center py-12 text-gray-400 dark:text-gray-500 text-sm">
            No search term data available for this period. Search terms require sufficient volume to populate.
          </div>
        ) : (
          <div>
            <div className="flex flex-wrap items-center gap-4 mb-4 pr-10">
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
                <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm text-blue-700 dark:text-blue-300">
                  <span className="font-semibold">{fmtCurrency(wastedSpend.total_non_converting_spend)}</span> spent on <span className="font-semibold">{wastedSpend.total_non_converting_terms.toLocaleString()}</span> search terms with no conversions this period
                </span>
              </div>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {wastedSpend.total_non_converting_clicks.toLocaleString()} clicks total
              </span>
            </div>

            <div className="flex items-start gap-2.5 px-4 py-3 mb-5 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 16v-4M12 8h.01" />
              </svg>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                These search terms had clicks but no tracked conversions this period. Some may be core business terms in early season or have conversions that aren't tracked online (phone bookings). Review with your account manager before making changes.
              </p>
            </div>

            <ThinScroll className="responsive-card-table-650-wrapper overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
              <table className="responsive-card-table-650 w-full text-sm min-w-[800px]" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '25%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '7%' }} />
                </colgroup>
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Search Term</th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Campaign</th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Ad Group</th>
                    <th className="text-right py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Clicks</th>
                    <th className="text-right py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Impr.</th>
                    <th className="text-right py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">CTR</th>
                    <th className="text-right py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Cost</th>
                    <th className="text-right py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Conv.</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedItems.map((item, index) => (
                    <tr key={index} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                      <td data-label="Search Term" className="py-2.5 px-3 text-gray-900 dark:text-gray-100 font-medium truncate">{item.search_term}</td>
                      <td data-label="Campaign" className="py-2.5 px-3 text-gray-600 dark:text-gray-400 text-xs truncate">{item.campaign_name}</td>
                      <td data-label="Ad Group" className="py-2.5 px-3 text-gray-600 dark:text-gray-400 text-xs truncate">{item.adgroup_name}</td>
                      <td data-label="Clicks" className="py-2.5 px-3 text-right text-gray-700 dark:text-gray-300">{item.clicks.toLocaleString()}</td>
                      <td data-label="Impr." className="py-2.5 px-3 text-right text-gray-700 dark:text-gray-300">{item.impressions.toLocaleString()}</td>
                      <td data-label="CTR" className="py-2.5 px-3 text-right text-gray-700 dark:text-gray-300">{fmtPercent(item.ctr)}</td>
                      <td data-label="Cost" className="py-2.5 px-3 text-right text-gray-700 dark:text-gray-300">{fmtCurrency(item.cost)}</td>
                      <td data-label="Conv." className="py-2.5 px-3 text-right text-gray-700 dark:text-gray-300">{item.conversions}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ThinScroll>

            <Pagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              totalItems={totalItems}
              perPage={PER_PAGE}
              label="rows"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default NonConvertingTerms;
